import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Notification } from "../../../../@types/Notification";
import {
  IArchivePatientRequest,
  IPortalPatientListItem,
} from "../../../../@types/Patient";
import { Button } from "../../../../components/Button";
import { Checkbox } from "../../../../components/Checkbox";
import { TextArea } from "../../../../components/TextArea";
import { invalidateAllNoteCaches } from "../../../../hooks/mutations/notes";
import { PATIENT_SEARCH_LIST_QUERY_KEY } from "../../../../hooks/queries/patients";
import { patientService } from "../../../../services/patientService";
import { reportService } from "../../../../services/reportService";
import { mixpanelActions } from "../../../../utils/mixpanel";
import {
  CancelButton,
  CheckboxContainer,
  ErrorMessage,
  ModalContainer,
  OptionalNoteInputWrapper,
  OptionalNoteLabel,
  OptionalNoteRow,
  Row,
  SectionContainer,
  SubmitContainer,
  Title,
} from "./ArchivePatientModal.styles";

type ArchivePatientModalProps = {
  content: IPortalPatientListItem;
  isReactivate: boolean;
  onClose(notification?: Notification): void;
};

export function ArchivePatientModal({
  content,
  isReactivate,
  onClose,
}: ArchivePatientModalProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [unlinkDevices, setUnlinkDevices] = useState(false);
  const [downloadDischargeDocument, setDownloadDischargeDocument] =
    useState(false);
  const [optionalNote, setOptionalNote] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const isSubmittingRef = useRef(false);

  const triggerDischargeDownload = async (patientId: number) => {
    try {
      const result = await reportService.downloadDischargeSummaryReport(
        patientId
      );

      if (result.status < 200 || result.status >= 300) {
        return;
      }

      const contentDisposition = result.headers["content-disposition"];
      const fileName =
        contentDisposition?.match(/filename\*=UTF-8''(.+)/)?.[1] ||
        contentDisposition?.match(/filename=(.+);?/)?.[1] ||
        `discharge-summary-${patientId}.pdf`;

      const url = URL.createObjectURL(new Blob([result.data]));
      const link = Object.assign(document.createElement("a"), {
        href: url,
        download: fileName,
      });
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Archive already succeeded — silent fail on download is intentional so the user can still
      // retry the download from the archived patients list without seeing the archive as failed.
    }
  };

  mixpanelActions.track(
    `VisitedScreen: ${isReactivate ? "Reactivate" : "Archive"}PatientModal`
  );

  const handleArchivePatient = async (sendEmail: boolean) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      setIsLoading(true);
      setErrorMessage("");

      const trimmedNote = optionalNote.trim();
      const request: IArchivePatientRequest = {
        patientId: content.id,
        sendEmail,
        unlinkDevices,
        ...(trimmedNote && !isReactivate ? { optionalNote: trimmedNote } : {}),
      };

      const defaultErrorMessage = isReactivate
        ? t("patientList_archiveModal_error_reactivate")
        : t("patientList_archiveModal_error_archive");

      try {
        const result = await patientService.archivePatient(request);

        if (result.status >= 200 && result.status < 300) {
          queryClient.invalidateQueries({
            queryKey: PATIENT_SEARCH_LIST_QUERY_KEY,
          });

          // The optional archive note becomes a discharge note on the patient's
          // timeline. Use the shared helper so the notes caches are invalidated
          // with refetchType: "all" — the global QueryClient sets
          // refetchOnMount: false, so a plain invalidate would leave the
          // (inactive) patient-page note list stale until a hard refresh.
          if (trimmedNote && !isReactivate) {
            invalidateAllNoteCaches(queryClient);
          }

          // Keep the button in loading state while the discharge PDF is downloading so the user
          // sees one continuous progress indicator and the modal stays open until everything is
          // done. If the checkbox isn't ticked, this is effectively a no-op.
          if (!isReactivate && downloadDischargeDocument) {
            await triggerDischargeDownload(content.id);
          }

          setIsLoading(false);

          const notification: Notification = {
            show: true,
            message: t(
              isReactivate
                ? "patientList_archiveModal_reactivated"
                : "patientList_archiveModal_archived"
            ),
            type: "success",
            width: "max-content",
          };
          onClose(notification);
        } else {
          setIsLoading(false);
          const errorData = result.data as unknown as {
            errors?: { message?: string }[];
          };
          const responseError = errorData?.errors?.[0]?.message;
          setErrorMessage(responseError || defaultErrorMessage);
        }
      } catch {
        setIsLoading(false);
        setErrorMessage(defaultErrorMessage);
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <ModalContainer>
      <Title>
        {isReactivate
          ? t("patientList_archiveModal_title_reactivate")
          : t("patientList_archiveModal_title_archive")}
      </Title>

      <SectionContainer>
        <Row>
          <p>{t("patientList_archiveModal_name")}</p>
          <p>{`${content.firstName} ${content.lastName}`}</p>
        </Row>
        <Row>
          <p>{t("patientList_archiveModal_hospitalId")}</p>
          <p>{content.hospitalPatientId ?? ""}</p>
        </Row>
        <Row>
          <p>{t("patientList_archiveModal_signUpDate")}</p>
          <p>{content.signUpDate}</p>
        </Row>
        {!isReactivate && (
          <OptionalNoteRow>
            <OptionalNoteLabel>
              {t("patientList_archiveModal_optionalNote")}
            </OptionalNoteLabel>
            <OptionalNoteInputWrapper>
              <TextArea
                name="archive-optional-note"
                value={optionalNote}
                placeholder={t(
                  "patientList_archiveModal_optionalNotePlaceholder"
                )}
                maxLength={2000}
                rows={4}
                cols={30}
                backgroudColor="#FFFFFF"
                onChange={(event) => setOptionalNote(event.target.value)}
              />
            </OptionalNoteInputWrapper>
          </OptionalNoteRow>
        )}
      </SectionContainer>

      {!isReactivate ? (
        <>
          <CheckboxContainer>
            <Checkbox
              label=""
              onChange={(value) => setUnlinkDevices(value)}
              checked={unlinkDevices}
            />
            <p>
              {/* Named component rather than <0>: the bold run moves within the
                  sentence when this is translated, and a name survives that. */}
              <Trans
                i18nKey="patientList_archiveModal_unlinkDevices"
                components={{ bold: <b style={{ fontWeight: "600" }} /> }}
              />
            </p>
          </CheckboxContainer>
          <CheckboxContainer>
            <Checkbox
              label=""
              onChange={(value) => setDownloadDischargeDocument(value)}
              checked={downloadDischargeDocument}
            />
            <p>
              <Trans
                i18nKey="patientList_archiveModal_downloadDischargeDocument"
                components={{ bold: <b style={{ fontWeight: "600" }} /> }}
              />
            </p>
          </CheckboxContainer>
        </>
      ) : null}

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <SubmitContainer>
        <Button
          label={
            isReactivate
              ? t("patientList_archiveModal_submit_reactivate")
              : t("patientList_archiveModal_submit_archive")
          }
          type="submit"
          onClick={() => {
            handleArchivePatient(true);
            mixpanelActions.track(
              `User Action: ${
                isReactivate
                  ? "ReactivateAndSendEmailButton"
                  : "ArchiveAndSendEmailButton"
              }`
            );
          }}
          isLoading={isLoading}
        />

        <CancelButton
          onClick={() => {
            if (isReactivate) {
              onClose();
            } else {
              handleArchivePatient(false);
            }

            mixpanelActions.track(
              `User Action: ${
                isReactivate ? "ReactivateCancelButton" : "ArchiveButton"
              }`
            );
          }}
          style={{ marginLeft: "-30px" }}
        >
          {isReactivate
            ? t("patientList_archiveModal_cancel_reactivate")
            : t("patientList_archiveModal_cancel_archive")}
        </CancelButton>
      </SubmitContainer>
    </ModalContainer>
  );
}
